import React, { useState, useEffect } from 'react';
import '../../styles/onboardingFlow/onboarding/onboarding.scss';
import OnboardingSidebar from './OnboardingSidebar';
import OnboardingMain from './OnboardingMain';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import {
  migrateOnboardingSteps,
  setAiOnboardingNextStep,
} from '../../store/reducers/userSlice';

const Onboarding = () => {
  const dispatch = useDispatch();
  const reduxCurrentStep = useSelector(
    (state: RootState) => state.user.ai_onboarding.currentStep
  );
  const completedSteps = useSelector(
    (state: RootState) => state.user.ai_onboarding.steps
  );
  const allShops = useSelector(
    (state: RootState) => state.user.profile.all_shops
  );

  const hasAtLeastOneShop = Array.isArray(allShops) && allShops.length > 0;

  const [currentStep, setCurrentStep] = useState(reduxCurrentStep);

  // Migrate persisted 4-step state to 5-step structure
  useEffect(() => {
    if (completedSteps.length !== 5) {
      dispatch(migrateOnboardingSteps());
    }
  }, [completedSteps.length, dispatch]);

  useEffect(() => {
    setCurrentStep(reduxCurrentStep);
  }, [reduxCurrentStep]);

  // Steps 1-2 don't require shops; steps 3-5 require at least one shop
  useEffect(() => {
    if (!hasAtLeastOneShop && currentStep > 2) {
      setCurrentStep(2);
      dispatch(setAiOnboardingNextStep(2));
    }
  }, [hasAtLeastOneShop, currentStep, dispatch]);

  const isStepCompleted = (stepNumber: number) => {
    return completedSteps[stepNumber - 1]?.isCompleted || false;
  };

  const isStepAccessible = (stepNumber: number) => {
    // Steps 1 and 2 are always accessible
    if (stepNumber <= 2) return true;

    // Steps 3-5 require at least one shop
    if (!hasAtLeastOneShop) return false;

    const isCompleted = completedSteps[stepNumber - 1]?.isCompleted || false;
    if (isCompleted) return true;

    // Find the last completed step number
    let lastCompletedStep = 0;
    for (let i = completedSteps.length - 1; i >= 0; i--) {
      if (completedSteps[i]?.isCompleted) {
        lastCompletedStep = i + 1;
        break;
      }
    }

    // Allow access to the step immediately after the last completed step
    const nextAvailableStep = lastCompletedStep + 1;
    return stepNumber === nextAvailableStep;
  };

  const handleStepChange = (stepNumber: number) => {
    if (isStepAccessible(stepNumber)) {
      setCurrentStep(stepNumber);
      dispatch(setAiOnboardingNextStep(stepNumber));
    }
  };

  return (
    <div className='onboarding'>
      <div className='onboarding__sidebar'>
        <OnboardingSidebar
          currentStep={currentStep}
          onStepChange={handleStepChange}
          isStepCompleted={isStepCompleted}
          isStepAccessible={isStepAccessible}
        />
      </div>
      <div className='onboarding__main'>
        <OnboardingMain
          currentStep={currentStep}
          onStepChange={handleStepChange}
        />
      </div>
    </div>
  );
};

export default Onboarding;
